import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { 
  createChart, 
  IChartApi, 
  CandlestickData, 
  UTCTimestamp, 
  HistogramData,
} from "lightweight-charts";
import { fromSatoshi } from "../../sdk/utils/math";

export interface KlineChartProps {
  initialData?: CandlestickData[]; // Optional historical data
  symbol?: string; // e.g., "BTC/USDC"
  height?: number; // Chart height in pixels
  showVolume?: boolean; // Whether to show volume histogram
}

export interface KlineChartRef {
  updateChart: (kline: KlineUpdate) => void;
}

export interface KlineUpdate {
  openTime?: number; // Unix timestamp in milliseconds
  closeTime?: number; // Unix timestamp in milliseconds
  open: string | number;
  high: string | number;
  low: string | number;
  close: string | number;
  volume?: string | number;
}

const KlineChart = forwardRef<KlineChartRef, KlineChartProps>(
  ({ initialData = [], symbol = "BTC/USDC", height = 500, showVolume = true }, ref) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candlestickSeriesRef = useRef<any>(null);
    const volumeSeriesRef = useRef<any>(null);
    const [currentPrice, setCurrentPrice] = useState<number | null>(null);

    // Convert satoshi string to decimal number
    const parsePrice = (price: string | number | undefined | null): number => {
      // Handle undefined/null
      if (price === undefined || price === null) {
        console.warn("[KlineChart] Invalid price: undefined or null");
        return 0;
      }
      
      if (typeof price === "number") {
        if (isNaN(price) || !isFinite(price) || price < 0) {
          console.warn("[KlineChart] Invalid price number:", price);
          return 0;
        }
        return price;
      }
      
      if (typeof price === "string") {
        // Validate string is not empty
        if (price.trim() === "") {
          console.warn("[KlineChart] Invalid price: empty string");
          return 0;
        }
        try {
          const parsed = fromSatoshi(price);
          if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
            console.warn("[KlineChart] Invalid price after conversion:", price, "->", parsed);
            return 0;
          }
          return parsed;
        } catch (error) {
          console.error("[KlineChart] Error parsing price:", price, error);
          return 0;
        }
      }
      
      // Fallback for any other type
      console.warn("[KlineChart] Invalid price type:", typeof price, price);
      return 0;
    };

    // Convert kline update to chart data format
    const convertKlineToBar = (kline: KlineUpdate): CandlestickData | null => {
      // Validate required fields
      if (
        kline.open === undefined || kline.open === null ||
        kline.high === undefined || kline.high === null ||
        kline.low === undefined || kline.low === null ||
        kline.close === undefined || kline.close === null
      ) {
        console.warn("[KlineChart] Missing required OHLC fields:", kline);
        return null;
      }
      
      const time = Math.floor((kline.openTime ?? kline.closeTime ?? Date.now()) / 1000) as UTCTimestamp;
      const open = parsePrice(kline.open);
      const high = parsePrice(kline.high);
      const low = parsePrice(kline.low);
      const close = parsePrice(kline.close);
      
      // Validate parsed prices are valid
      if (open <= 0 || high <= 0 || low <= 0 || close <= 0) {
        console.warn("[KlineChart] Invalid kline data - non-positive prices detected:", {
          original: kline,
          parsed: { open, high, low, close },
        });
        return null;
      }
      
      // Validate and correct data integrity (OHLC requirements)
      // High must be >= max(open, high, low, close)
      // Low must be <= min(open, high, low, close)
      const actualHigh = Math.max(open, high, low, close);
      const actualLow = Math.min(open, high, low, close);
      
      if (high !== actualHigh || low !== actualLow) {
        console.warn("[KlineChart] Kline data corrected - high/low were not extremes:", {
          original: { high, low },
          corrected: { high: actualHigh, low: actualLow },
        });
      }
      
      return {
        time,
        open,
        high: actualHigh,
        low: actualLow,
        close,
      };
    };

    // Convert kline update to volume data format
    const convertKlineToVolume = (kline: KlineUpdate): HistogramData | null => {
      if (!kline.volume) return null;
      
      const time = Math.floor((kline.openTime ?? kline.closeTime ?? Date.now()) / 1000) as UTCTimestamp;
      
      let volume: number;
      if (typeof kline.volume === "number") {
        if (isNaN(kline.volume) || !isFinite(kline.volume) || kline.volume < 0) {
          console.warn("[KlineChart] Invalid volume number:", kline.volume);
          return null;
        }
        volume = kline.volume;
      } else if (typeof kline.volume === "string" && kline.volume.trim() !== "") {
        try {
          volume = fromSatoshi(kline.volume);
          if (isNaN(volume) || !isFinite(volume) || volume < 0) {
            console.warn("[KlineChart] Invalid volume after conversion:", kline.volume, "->", volume);
            return null;
          }
        } catch (error) {
          console.error("[KlineChart] Error converting volume from satoshi:", kline.volume, error);
          return null;
        }
      } else {
        console.warn("[KlineChart] Invalid volume type:", typeof kline.volume, kline.volume);
        return null;
      }
      
      const close = parsePrice(kline.close);
      const open = parsePrice(kline.open);
      
      // Validate prices are valid before determining color
      if (close <= 0 || open <= 0) {
        console.warn("[KlineChart] Invalid prices for volume color calculation:", { open, close });
        return null;
      }
      
      const isUp = close >= open;

      return {
        time,
        value: volume,
        color: isUp ? "#26a69a80" : "#ef535080", // Semi-transparent colors matching candlesticks
      };
    };

    // Function to update with new Kline bar (exposed via ref)
    const updateChart = (kline: KlineUpdate) => {
      if (!candlestickSeriesRef.current) {
        console.warn("[KlineChart] updateChart called before chart is initialized");
        return;
      }

      // Validate input
      if (!kline || typeof kline !== "object") {
        console.warn("[KlineChart] Invalid kline update data:", kline);
        return;
      }

      try {
        const bar = convertKlineToBar(kline);
        
        // If bar conversion failed, skip update
        if (!bar) {
          console.warn("[KlineChart] Failed to convert kline to bar, skipping update");
          return;
        }
        
        const volumeBar = showVolume ? convertKlineToVolume(kline) : null;

        candlestickSeriesRef.current.update(bar);
        setCurrentPrice(bar.close);

        if (showVolume && volumeSeriesRef.current && volumeBar) {
          volumeSeriesRef.current.update(volumeBar);
        }
      } catch (error) {
        console.error("[KlineChart] Error updating chart:", error);
      }
    };

    // Expose update function via ref
    useImperativeHandle(ref, () => ({
      updateChart,
    }));

    useEffect(() => {
      if (!chartContainerRef.current) return;

      // Clean up existing chart if it exists
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (error) {
          console.warn("[KlineChart] Error removing existing chart:", error);
        }
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        volumeSeriesRef.current = null;
      }

      let chart: IChartApi | null = null;
      let animationFrameId: number | null = null;
      let resizeHandler: (() => void) | null = null;

      const initializeChart = () => {
        if (!chartContainerRef.current) return;

        const container = chartContainerRef.current;
        const containerWidth = container.clientWidth || container.offsetWidth;
        const containerHeight = height || 500;

        // Validate dimensions - require at least 100px width
        if (!containerWidth || containerWidth < 100) {
          console.warn("[KlineChart] Container not ready, waiting for layout...", {
            clientWidth: container.clientWidth,
            offsetWidth: container.offsetWidth,
          });
          // Use requestAnimationFrame to wait for next layout
          animationFrameId = requestAnimationFrame(initializeChart);
          return;
        }

        if (containerHeight <= 0) {
          console.warn("[KlineChart] Invalid height:", containerHeight);
          return;
        }

        let candlestickSeriesInstance: any;
        let volumeSeries: any = null;

        try {
          // Create chart
          chart = createChart(container, {
            width: containerWidth,
            height: containerHeight,
            layout: {
              background: { color: "#1e1e1e" },
              textColor: "#d1d4dc",
            },
            grid: {
              vertLines: { color: "#2a2e39" },
              horzLines: { color: "#2a2e39" },
            },
            crosshair: {
              mode: 1, // Normal mode
            },
            rightPriceScale: {
              borderColor: "#2a2e39",
            },
            timeScale: {
              borderColor: "#2a2e39",
              timeVisible: true,
              secondsVisible: false,
            },
          });

          // Add candlestick series - using type assertion to work with v5 API
          // Try the typed method first, fallback to string-based API
          if (typeof (chart as any).addCandlestickSeries === "function") {
            candlestickSeriesInstance = (chart as any).addCandlestickSeries({
              upColor: "#26a69a",
              downColor: "#ef5350",
              borderVisible: false,
              wickUpColor: "#26a69a",
              wickDownColor: "#ef5350",
            });
          } else {
            // Fallback to string-based API for older versions
            candlestickSeriesInstance = (chart as any).addSeries("Candlestick", {
              upColor: "#26a69a",
              downColor: "#ef5350",
              borderVisible: false,
              wickUpColor: "#26a69a",
              wickDownColor: "#ef5350",
            });
          }

          // Add volume histogram if enabled
          if (showVolume) {
            if (typeof (chart as any).addHistogramSeries === "function") {
              volumeSeries = (chart as any).addHistogramSeries({
                color: "#26a69a80",
                priceFormat: {
                  type: "volume",
                },
                priceScaleId: "",
              });
            } else {
              // Fallback to string-based API
              volumeSeries = (chart as any).addSeries("Histogram", {
                color: "#26a69a80",
                priceFormat: {
                  type: "volume",
                },
                priceScaleId: "",
              });
            }
          }
        } catch (error) {
          console.error("[KlineChart] Error creating chart or series:", error);
          return;
        }

        // Load initial/historical data if any
        if (initialData.length > 0 && candlestickSeriesInstance) {
          try {
            candlestickSeriesInstance.setData(initialData);
            // Set current price from the last data point
            setCurrentPrice(initialData[initialData.length - 1].close);
          } catch (error) {
            console.error("[KlineChart] Error setting initial data:", error);
          }
        }

        chartRef.current = chart;
        candlestickSeriesRef.current = candlestickSeriesInstance;
        volumeSeriesRef.current = volumeSeries;

        // Handle resize
        resizeHandler = () => {
          if (chartContainerRef.current && chartRef.current) {
            const newWidth = chartContainerRef.current.clientWidth || chartContainerRef.current.offsetWidth;
            if (newWidth > 0) {
              chartRef.current.applyOptions({ width: newWidth });
            }
          }
        };
        window.addEventListener("resize", resizeHandler);
      };

      // Start initialization
      initializeChart();

      // Cleanup function
      return () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        if (resizeHandler) {
          window.removeEventListener("resize", resizeHandler);
        }
        if (chart) {
          try {
            chart.remove();
          } catch (error) {
            console.error("[KlineChart] Error removing chart:", error);
          }
        }
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        volumeSeriesRef.current = null;
      };
    }, [height, showVolume]); // Re-create chart if height or showVolume changes

    // Update initial data when it changes (but don't recreate chart)
    useEffect(() => {
      if (candlestickSeriesRef.current && initialData.length > 0) {
        try {
          candlestickSeriesRef.current.setData(initialData);
          setCurrentPrice(initialData[initialData.length - 1].close);
        } catch (error) {
          console.error("[KlineChart] Error setting initial data:", error);
        }
      }
    }, [initialData]);

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <div
          style={{
            marginBottom: "0.5rem",
            fontSize: "1.2rem",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {symbol} Live Kline Chart
            {currentPrice !== null && ` - Current: $${currentPrice.toFixed(2)}`}
          </span>
        </div>
        <div ref={chartContainerRef} style={{ position: "relative", width: "100%" }} />
      </div>
    );
  }
);

KlineChart.displayName = "KlineChart";

export default KlineChart;
